import { api, unwrap } from '../../shared/api/client';
import { ApiError } from '../../shared/api/errors';
import type { components } from '../../shared/api/schema.gen';

export type TLibraryItem = components['schemas']['LibraryItemResDto'];
export type TLibraryKind = TLibraryItem['kind'];
export type TLibraryVersion = components['schemas']['LibraryVersionResDto'];
export type TLibraryVersionSummary = components['schemas']['LibraryVersionSummaryResDto'];
export type TLibraryDiff = components['schemas']['LibraryDiffResDto'];
export type TLibraryDiffLine = TLibraryDiff['lines'][number];
export type TLibraryMessage = components['schemas']['LibraryMessageDto'];
export type TLibraryItemCreate = components['schemas']['CreateLibraryItemReqDto'];
export type TPromptTestRequest = components['schemas']['PromptTestReqDto'];
export type TPromptTest = components['schemas']['PromptTestResDto'];
export type TLibraryUse = TLibraryItem['flows'][number];
export type TJsonValue = components['schemas']['JsonValue'];

export const libraryKeys = {
  all: ['library'] as const,
  list: (kind?: TLibraryKind) => ['library', 'list', kind ?? 'all'] as const,
  /** One folder (`root` for the top level), or every folder when searching by name. */
  listIn: (where: ILibraryWhere) =>
    ['library', 'list', 'in', where.folderId ?? 'all', where.q ?? ''] as const,
  item: (id: string) => ['library', 'item', id] as const,
  versions: (id: string) => ['library', 'item', id, 'versions'] as const,
  version: (id: string, version: number | null) =>
    ['library', 'item', id, 'versions', version] as const,
  diff: (id: string, from: number, to: number) =>
    ['library', 'item', id, 'diff', from, to] as const,
  models: (credentialId: string) => ['library', 'models', credentialId] as const,
};

export const listLibrary = (kind?: TLibraryKind) =>
  unwrap(api.GET('/v1/library', { params: { query: kind ? { kind } : {} } }));

export interface ILibraryWhere {
  folderId?: string;
  q?: string;
}

export const listLibraryIn = (where: ILibraryWhere) =>
  unwrap(api.GET('/v1/library', { params: { query: where } }));

// A taken name is the name field's problem, so the form puts it there.
const onName = (error: unknown): never => {
  const taken = ['library.name_taken', 'transforms.name_taken'];
  if (!(error instanceof ApiError) || !taken.includes(error.code ?? '')) throw error;
  throw new ApiError(
    {
      code: error.code,
      httpCode: error.status,
      requestId: error.requestId,
      timestamp: new Date().toISOString(),
      path: '/v1/library',
      method: 'POST',
      message: error.message,
      details: [{ field: 'name', message: error.message }],
    },
    error.status,
  );
};

export const createLibraryItem = (body: TLibraryItemCreate): Promise<TLibraryItem> =>
  unwrap(api.POST('/v1/library', { body })).catch(onName);

export const getLibraryItem = (id: string): Promise<TLibraryItem> =>
  unwrap(api.GET('/v1/library/{id}', { params: { path: { id } } }));

export const renameLibraryItem = (
  id: string,
  body: { name?: string; description?: string },
): Promise<TLibraryItem> =>
  unwrap(api.PATCH('/v1/library/{id}', { params: { path: { id } }, body }));

export const deleteLibraryItem = (id: string): Promise<void> =>
  unwrap(api.DELETE('/v1/library/{id}', { params: { path: { id } } })).then(() => undefined);

export const listLibraryVersions = (id: string): Promise<TLibraryVersionSummary[]> =>
  unwrap(api.GET('/v1/library/{id}/versions', { params: { path: { id } } }));

export const getLibraryVersion = (id: string, version: number): Promise<TLibraryVersion> =>
  unwrap(api.GET('/v1/library/{id}/versions/{version}', { params: { path: { id, version } } }));

export const createLibraryVersion = (
  id: string,
  body: { code?: string; messages?: TLibraryMessage[]; comment?: string },
): Promise<TLibraryVersion> =>
  unwrap(api.POST('/v1/library/{id}/versions', { params: { path: { id } }, body }));

export const diffLibraryVersions = (id: string, from: number, to: number): Promise<TLibraryDiff> =>
  unwrap(
    api.GET('/v1/library/{id}/versions/{from}/diff/{to}', { params: { path: { id, from, to } } }),
  );

export const rollbackLibraryVersion = (
  id: string,
  version: number,
  comment: string,
): Promise<TLibraryVersion> =>
  unwrap(
    api.POST('/v1/library/{id}/versions/{version}/rollback', {
      params: { path: { id, version } },
      body: { comment },
    }),
  );

export const testPrompt = (body: TPromptTestRequest): Promise<TPromptTest> =>
  unwrap(api.POST('/v1/library/prompt-test', { body }));

// The models an AI account reaches, through the same dynamic list the AI step's Model uses.
export const listModels = (credentialId: string) =>
  unwrap(
    api.POST('/v1/flows/connector-options', {
      body: { connector: 'ai', action: 'generate', field: 'model', credentialId },
    }),
  );
